import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownLeft, ArrowUpRight, PhoneMissed, ArrowRightLeft,
  Coffee, PauseCircle, MicOff, Clock, BarChart3,
} from 'lucide-react';
import clsx from 'clsx';
import { Performance, type PerfRange, type PerformanceData } from '@/lib/api';
import { fmtMSS, fmtHHMMSS } from '@/lib/useTodayStats';
import { CallDonut, DonutLegend } from '@/components/workspace/CallDonut';
import { reasonLabel } from '@/components/softphone/BreakReasonPopover';
import type { BreakReason } from '@/store/softphone';

/**
 * Performans sayfası — temsilcinin kendi metrikleri, tarih aralıklı.
 * Veri: GET /api/performance/me?range= (activity_logs server-side agregasyon).
 *
 * Workspace "bugünün özeti"ne odaklanırken bu sayfa derinlemesine analiz:
 * gün/hafta/ay aralığı, inbound/outbound kırılımı, hold/mute, mola sebepleri,
 * günlük trend.
 */

const RANGES: { key: PerfRange; label: string }[] = [
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Bu Hafta' },
  { key: 'month', label: 'Bu Ay' },
];

export default function PerformancePage() {
  const [range, setRange] = useState<PerfRange>('today');
  const { data, isLoading } = useQuery({
    queryKey: ['performance', range],
    queryFn: () => Performance.me(range),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  return (
    <div className="px-5 py-3 max-w-[1200px] mx-auto">
      {/* Aralık seçici — başlık/breadcrumb kaldırıldı (sidebar zaten gösteriyor,
          compact). Sadece Bugün/Bu Hafta/Bu Ay kontrolü kaldı, sağ-hizalı. */}
      <header className="flex items-center justify-end mb-2">
        <div className="inline-flex bg-surface ring-1 ring-slate-200 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-[12px] font-medium transition',
                range === r.key ? 'bg-brand-500 text-[#08140d] shadow-sm' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {isLoading || !data ? (
        <PerfSkeleton />
      ) : (
        <PerfBody data={data} />
      )}
    </div>
  );
}

function PerfBody({ data }: { data: PerformanceData }) {
  const dAnswered = { label: 'Cevap',    value: data.answered,      colorClass: 'text-emerald-500' };
  const dMissed   = { label: 'Cevapsız', value: data.missed,        colorClass: 'text-red-500' };
  const dOutbound = { label: 'Giden',    value: data.outboundCount, colorClass: 'text-amber-500' };
  const donutRings = [dAnswered, dMissed];           // halkalar: sadece gelen
  const donutLegend = [dAnswered, dMissed, dOutbound]; // legend: 3'ü de
  const trendCalls = data.trend.map((b) => b.answered + b.outbound);
  // Bugün=saatlik, hafta=günlük, ay=haftalık. Backend zaten doğru granülaritede
  // sıfır-dolu döner; bugün hiç hareket yoksa trend [] → büyük "toplam" fallback.
  const showTrend = data.trend.length > 0;
  const trendTitle = data.range === 'today' ? 'Saatlik Dağılım'
    : data.range === 'month' ? 'Haftalık Çağrı Trendi'
    : 'Günlük Çağrı Trendi';

  return (
    <>
      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
        <Kpi icon={<ArrowDownLeft size={15} />} tone="emerald" label="Cevaplanan" value={data.answered} />
        <Kpi icon={<PhoneMissed size={15} />}   tone="red"     label="Cevapsız"   value={data.missed} />
        <Kpi icon={<ArrowUpRight size={15} />}  tone="amber"   label="Giden"      value={data.outboundCount} />
        <Kpi icon={<Clock size={15} />}         tone="brand"   label="Toplam Görüşme" value={fmtHHMMSS(data.totalTalkSec)} mono />
      </div>

      {/* Donut + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 mb-2">
        <section className="lg:col-span-2 rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 flex flex-col items-center justify-center shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 self-start mb-1">Çağrı Dağılımı</div>
          <CallDonut segments={donutRings} size={120} />
          <DonutLegend segments={donutLegend} />
        </section>

        <section className="lg:col-span-3 rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex flex-col">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
            <BarChart3 size={11} className="text-brand-500" />
            {showTrend ? trendTitle : 'Bugünkü Tempo'}
          </div>
          {showTrend ? (
            <TrendBars buckets={data.trend} values={trendCalls} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-center min-h-[120px]">
              <div>
                <div className="text-[40px] font-bold text-slate-900 tabular-nums leading-none">{data.answered + data.outboundCount}</div>
                <div className="text-[12px] text-slate-400 mt-1">bugün toplam çağrı</div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Inbound vs Outbound */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
        <DirectionCard
          title="Gelen Çağrılar" icon={<ArrowDownLeft size={14} />} tone="emerald"
          count={data.inbound.count} talkSec={data.inbound.talkSec}
          avgSec={data.inbound.avgSec} maxSec={data.inbound.maxSec}
          holdSec={data.inbound.holdSec} muteSec={data.inbound.muteSec}
        />
        <DirectionCard
          title="Giden Çağrılar" icon={<ArrowUpRight size={14} />} tone="amber"
          count={data.outbound.count} talkSec={data.outbound.talkSec}
          avgSec={data.outbound.avgSec} maxSec={data.outbound.maxSec}
          holdSec={data.outbound.holdSec} muteSec={data.outbound.muteSec}
        />
      </div>

      {/* Aktarım + Mola sayısı + Mola kırılımı — tek compact satır.
          Toplam Bekletme/Sessize çıkarıldı (artık yön kartlarında ayrı ayrı var). */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Micro icon={<ArrowRightLeft size={13} />} label="Aktarım"    value={String(data.transfers)} />
        <Micro icon={<Coffee size={13} />}         label="Mola Sayısı" value={String(data.breakCount)} />
        {/* Mola kırılımı — sebep bazlı SAYI + TOPLAM SÜRE (eski STATUS kartı) */}
        <section className="col-span-2 rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <Coffee size={11} className="text-amber-500" /> Mola Kırılımı
          </div>
          {data.breaks.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {data.breaks.map((b) => (
                <div key={b.reason} className="inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1 rounded-lg bg-amber-50 ring-1 ring-amber-200">
                  <span className="text-[11.5px] font-medium text-amber-800">{reasonLabel(b.reason as BreakReason)}</span>
                  <span className="text-[11.5px] font-bold text-amber-700 font-mono tabular-nums" title="Toplam süre">{fmtHHMMSS(b.totalSec)}</span>
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-md bg-amber-200/70 text-[10.5px] font-bold text-amber-800 tabular-nums" title="Kullanım sayısı">{b.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11.5px] text-slate-400">Bu aralıkta mola yok</div>
          )}
        </section>
      </div>
    </>
  );
}

/* ─── Daily trend bar chart (labeled) ─────────────────────────── */

function TrendBars({ buckets, values }: { buckets: PerformanceData['trend']; values: number[] }) {
  const max = Math.max(...values, 1);
  const n = buckets.length;
  return (
    <div className="flex-1 flex items-end gap-1.5 min-h-[120px] pt-2">
      {buckets.map((b, i) => {
        const h = Math.round((values[i] / max) * 100);
        const isLast = i === n - 1;
        // Sparse labels: çok sütun varsa (saatlik) seyrek etiketle
        const showLabel = n <= 10 || i === 0 || i === n - 1 || i % Math.ceil(n / 7) === 0;
        return (
          <div key={b.key} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${b.label}: ${values[i]} çağrı`}>
            <div className="w-full flex items-end justify-center" style={{ height: 100 }}>
              <div
                className={clsx('w-full max-w-[28px] rounded-t transition-all', isLast ? 'bg-brand-500' : 'bg-brand-200')}
                style={{ height: `${Math.max(h, 2)}%` }}
              />
            </div>
            <div className="text-[9px] text-slate-400 tabular-nums h-3 leading-3 truncate w-full text-center">
              {showLabel ? b.label : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Cards ───────────────────────────────────────────────────── */

function Kpi({ icon, tone, label, value, mono }: {
  icon: React.ReactNode; tone: 'emerald' | 'red' | 'amber' | 'brand';
  label: string; value: React.ReactNode; mono?: boolean;
}) {
  const iconCls = {
    emerald: 'bg-emerald-50 text-emerald-600',
    red:     'bg-red-50 text-red-600',
    amber:   'bg-amber-50 text-amber-600',
    brand:   'bg-brand-50 text-brand-600',
  }[tone];
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5">
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', iconCls)}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10.5px] font-medium text-slate-500 uppercase tracking-widest">{label}</div>
        <div className={clsx('text-[22px] font-bold text-slate-900 tabular-nums leading-tight', mono && 'font-mono text-[17px]')}>{value}</div>
      </div>
    </div>
  );
}

function DirectionCard({ title, icon, tone, count, talkSec, avgSec, maxSec, holdSec, muteSec }: {
  title: string; icon: React.ReactNode; tone: 'emerald' | 'amber';
  count: number; talkSec: number; avgSec: number; maxSec: number; holdSec: number; muteSec: number;
}) {
  const head = tone === 'emerald' ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50';
  return (
    <section className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className={clsx('w-7 h-7 rounded-lg flex items-center justify-center', head)}>{icon}</span>
        <span className="text-[13px] font-semibold text-slate-900">{title}</span>
        <span className="ml-auto text-[22px] font-bold text-slate-900 tabular-nums">{count}</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-center mb-1.5">
        <Stat label="Toplam" value={fmtHHMMSS(talkSec)} />
        <Stat label="Ortalama" value={fmtMSS(avgSec)} />
        <Stat label="En Uzun" value={fmtMSS(maxSec)} />
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-center">
        <Stat label="Bekletme" value={fmtMSS(holdSec)} icon={<PauseCircle size={11} />} />
        <Stat label="Sessize Alma" value={fmtMSS(muteSec)} icon={<MicOff size={11} />} />
      </div>
    </section>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-slate-50 py-1.5">
      <div className="text-[14px] font-bold text-slate-900 font-mono tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5 flex items-center justify-center gap-1">
        {icon}{label}
      </div>
    </div>
  );
}

function Micro({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10.5px] font-medium text-slate-500 uppercase tracking-widest">{label}</div>
        <div className={clsx('text-[16px] font-bold text-slate-900 tabular-nums leading-tight', mono && 'font-mono text-[14px]')}>{value}</div>
      </div>
    </div>
  );
}

function PerfSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-[76px] rounded-2xl bg-slate-100" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 h-[240px] rounded-2xl bg-slate-100" />
        <div className="lg:col-span-3 h-[240px] rounded-2xl bg-slate-100" />
      </div>
    </div>
  );
}
